import { useEffect, useMemo } from "react";
import {
  resolveBranding,
  type BrandingConfig,
  type ResolvedBranding,
  type TemplateVars,
} from "@/lib/branding";
import type { UiTranslations } from "@/lib/ui-translations";

/**
 * Entry point for firm branding in the form.
 *
 * Give it a `BrandingConfig` (or null for the stock Formio look) and it returns
 * the CSS variables to put on the form's root container, the logo, and the
 * welcome copy with `{prenom}`, `{nom}` and `{cabinet}` already resolved.
 *
 * Where the config comes from is deliberately not this hook's business — see
 * `getBrandingConfig()` in `@/lib/branding`.
 */
export function useBranding(
  config: BrandingConfig | null | undefined,
  vars: TemplateVars,
  t: UiTranslations,
): ResolvedBranding {
  const { prenom, nom, cabinet } = vars;
  return useMemo(
    () => resolveBranding(config, { prenom, nom, cabinet }, t),
    [config, prenom, nom, cabinet, t],
  );
}

/**
 * Paints the firm's tokens on `<html>`, which is what makes the branding follow
 * the client through the whole form instead of stopping at the welcome screen:
 * the wizard, the completion screen and the Radix dropdowns and dialogs that
 * portal out to `<body>` all inherit from the document element.
 *
 * Also paints `body` so iOS rubber-band scrolling shows the firm's background
 * rather than a white band, and stamps `data-brand-card`, which arms the
 * `.bg-card` remap in index.css.
 *
 * Everything is removed on unmount and whenever the config goes away, so a firm
 * without the add-on is left on the stock theme, untouched.
 */
export function useApplyBranding(branding: ResolvedBranding): void {
  const { rootVars, brandsCards, backgroundColor } = branding;

  useEffect(() => {
    const root = document.documentElement;
    const entries = Object.entries(rootVars);
    for (const [name, value] of entries) root.style.setProperty(name, value);
    if (brandsCards) root.setAttribute("data-brand-card", "");

    return () => {
      for (const [name] of entries) root.style.removeProperty(name);
      root.removeAttribute("data-brand-card");
    };
  }, [rootVars, brandsCards]);

  useEffect(() => {
    if (!backgroundColor) return;
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = backgroundColor;
    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, [backgroundColor]);
}
